import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { useForm } from 'react-hook-form';
import { API_URL } from '../../../actions/index';
import { postMediaAction, getMediasPageAction } from '../../../actions/mediasActions';
import { useGlobalContext } from '../../context/GlobalContext';
import { companiesColors, langsOptions } from '../../../utils/utils';
import { resourceCompanies } from '../../../utils/resourceCompanies';
import '../medias/medias.scss';

export default function ResourceFileForm({
  companyId,
  theme,
  lang,
  fileType,
  withThumbnail = true,
  withCompanySelect = false,
  defaultCompanyId,
  onSaved,
  onCancel,
}) {
  const { handleSubmit, register, errors } = useForm();
  const [, dispatch] = useGlobalContext();
  const [fileSizeError, setFileSizeError] = useState();
  const [selectedThumbnail, setSelectedThumbnail] = useState();
  const [selectedCompany, setSelectedCompany] = useState(
    defaultCompanyId
      ? resourceCompanies.find((c) => c.companyId === defaultCompanyId)
      : resourceCompanies[0]
  );
  const [selectedLangs, setSelectedLangs] = useState(langsOptions.filter((l) => l.value === lang));
  const [showThumbnailList, setShowThumbnailList] = useState(false);
  const [thumbnailList, setThumbnailList] = useState([]);

  const companyOptions = resourceCompanies.map((c) => ({
    value: c.companyId,
    label: c.name,
  }));

  const customStyles = {
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: companiesColors[theme],
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'white',
    }),
    container: (provided) => ({
      ...provided,
      width: '100%',
      marginTop: 7,
      marginBottom: 20,
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0px 5px',
    }),
    control: (provided, state) => ({
      ...provided,
      boxShadow: 'none',
      minHeight: 44,
      borderRadius: 10,
      border: state.isFocused ? `1px solid ${companiesColors[theme]}` : '1px solid #E0E3EF',
    }),
  };

  useEffect(() => {
    if (!showThumbnailList || !companyId) {
      return;
    }
    getMediasPageAction(dispatch, {
      companyId,
      type: 'thumbnails',
      page: 1,
      limit: 50,
    }).then((result) => setThumbnailList(result.files));
    // eslint-disable-next-line
  }, [showThumbnailList, companyId]);

  const submitMedia = async (values) => {
    if (withThumbnail && !selectedThumbnail) {
      setFileSizeError('Sélectionner une miniature');
      return;
    }

    const formData = new FormData();
    const langs = selectedLangs?.map((l) => l.value)?.join(',');
    const targetCompany = withCompanySelect ? selectedCompany?.companyId : defaultCompanyId;

    formData.append('file', values.file[0], values.file[0].name);
    formData.append('company', targetCompany);
    formData.append('title', values.title);
    formData.append('description', values.description || '');
    formData.append('alt', values.title);
    formData.append('type', fileType);
    formData.append('isThumbnail', false);
    formData.append('isPriority', false);
    formData.append('langs', langs);
    if (selectedThumbnail) {
      formData.append('thumbnail', selectedThumbnail);
    }

    await postMediaAction(dispatch, formData);
    if (onSaved) {
      onSaved();
    }
  };

  function onChangeFile() {
    setFileSizeError();
    const fileInput = document.getElementById(`resource-file-${fileType}`);
    if (!fileInput?.files?.[0]) {
      return;
    }
    const file = fileInput.files[0];
    if (file.size / 1024 / 1024 > 5) {
      setFileSizeError('Les fichiers ne doivent pas être supérieurs à 5 MB');
      fileInput.value = '';
    }
    if (file.type !== 'application/pdf') {
      setFileSizeError('Seuls les fichiers PDF sont autorisés');
      fileInput.value = '';
    }
  }

  return (
    <form onSubmit={handleSubmit(submitMedia)} className="resource-file-form">
      {withCompanySelect && (
        <>
          <label>Société</label>
          <Select
            value={companyOptions.find((o) => o.value === selectedCompany?.companyId)}
            options={companyOptions}
            onChange={(option) => {
              setSelectedCompany(resourceCompanies.find((c) => c.companyId === option.value));
            }}
            styles={customStyles}
          />
        </>
      )}

      <label>Titre<span className={`${theme}-color required`}>*</span></label>
      <input
        name="title"
        ref={register({ required: 'Compléter le titre' })}
      />
      <p className="error">{errors?.title?.message}</p>

      <label>Description</label>
      <textarea name="description" ref={register()} />

      <label>Langues</label>
      <Select
        isMulti
        value={selectedLangs}
        options={langsOptions}
        onChange={setSelectedLangs}
        styles={customStyles}
      />

      <label>Fichier PDF<span className={`${theme}-color required`}>*</span></label>
      <input
        id={`resource-file-${fileType}`}
        name="file"
        type="file"
        accept="application/pdf"
        onChange={onChangeFile}
        ref={register({ required: 'Sélectionner un fichier' })}
      />
      <p className="error">{fileSizeError || errors?.file?.message}</p>

      {withThumbnail && (
        <>
          <label>Miniature<span className={`${theme}-color required`}>*</span></label>
          <div className="btn-container">
            <button
              type="button"
              className={`btn primary ${theme}-bg`}
              onClick={() => setShowThumbnailList(!showThumbnailList)}
            >
              {selectedThumbnail ? 'Changer la miniature' : 'Choisir une miniature'}
            </button>
          </div>
          {selectedThumbnail && (
            <img
              src={`${API_URL}/${thumbnailList.find((t) => t._id === selectedThumbnail)?.path}`}
              alt="miniature"
              style={{ maxWidth: 120, marginTop: 8 }}
            />
          )}
          {showThumbnailList && (
            <div className="thumbnail-list">
              {thumbnailList.map((thumb) => (
                <button
                  key={thumb._id}
                  type="button"
                  className={`thumbnail-item ${selectedThumbnail === thumb._id ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedThumbnail(thumb._id);
                    setShowThumbnailList(false);
                  }}
                >
                  <img src={`${API_URL}/${thumb.path}`} alt={thumb.alt} />
                </button>
              ))}
            </div>
          )}
        </>
      )}

      <div className="btn-container" style={{ marginTop: 16 }}>
        <button type="submit" className="btn success">Ajouter</button>
        {onCancel && (
          <button type="button" className="btn btn-cancel" onClick={onCancel}>Annuler</button>
        )}
      </div>
    </form>
  );
}
